import type { ScreenId } from './registry';

/**
 * Links the previous Pelago app's estate still emits, which the Galapagos
 * binary takes over when it ships as an in-place update of the same store
 * listing. Nothing in that estate is rewritten on the day of the update — the
 * scheme lives in stored notifications and emails, and the App Links hosts are
 * verified against the package name — so the app has to keep answering them
 * or every such link falls to a browser.
 *
 * Two families:
 *
 * - Custom scheme: `pelagohealth://{host}` (production) and
 *   `pelagohealth-dev://{host}` (development), where `{host}` names a legacy
 *   surface, not a Galapagos screen.
 * - Verified App Links (Android) / universal links (iOS) on two hosts.
 *   `app.pelagohealth.com/{surface}` carries the surface as its first path
 *   segment. `links.pelagohealth.com/{id}` is a campaign-tracking rewrite whose
 *   destination is only known after following its redirect; until a resolver
 *   exists it opens the app on the home surface rather than a browser.
 *
 * Translation produces a CANONICAL Galapagos URL (ADR-035 Rule 7), so every
 * downstream rule — registry lookup, params schema, routability, ownership,
 * telemetry — keeps running unchanged on the translated form. Query strings
 * are carried across untouched: the per-screen params schema strips what it
 * does not declare (ADR-035 Rule 6), and campaign attribution reads the raw
 * link before translation ever happens.
 */
export const LEGACY_DEEPLINK_SCHEMES = Object.freeze(['pelagohealth', 'pelagohealth-dev'] as const);

export const LEGACY_APP_LINK_HOSTS = Object.freeze([
  'app.pelagohealth.com',
  'links.pelagohealth.com',
] as const);

/**
 * Legacy surface → Galapagos screen. Surfaces with no Galapagos equivalent are
 * listed explicitly so the map documents every host the previous manifest
 * answered; each resolves to `home`, which is also the fallback for a surface
 * the previous app never named.
 */
export const LEGACY_SURFACE_SCREEN_IDS: Readonly<Record<string, ScreenId>> = Object.freeze({
  home: 'home',
  chat: 'care-team-chat',
  appointment: 'care-upcoming-calls',
  medication: 'medication',
  achievements: 'rewards',
  badges: 'rewards',
  checkin: 'home',
  'daily-checkin': 'home',
  'checkin-alcohol': 'home',
  goals: 'home',
  rate: 'home',
  library: 'home',
  insights: 'home',
  'app.pelagohealth.com': 'home',
});

const LEGACY_FALLBACK_SCREEN_ID: ScreenId = 'home';

// Custom-scheme branch: the same regex pre-parse the canonical grammar uses,
// because `URL` diverges between Node and Hermes on non-special schemes. The
// https branches below use `URL`, exactly as the grammar does for universal
// links, so the two modules agree on every URL shape.
const LEGACY_SCHEME_RE =
  /^(pelagohealth|pelagohealth-dev):\/{2}([a-z0-9.-]+)(?:\/[^?]*)?(?:\?(.*))?$/i;

// Some Android launch paths deliver scheme links path-style with an empty
// host (`pelagohealth:///chat`); the same normalisation the capture path
// applies to `galapagos:///…` links, done here so the surface is read from
// the first path segment instead of an empty host.
function normalizeLegacyPathStyle(url: string): string {
  return url.replace(/^(pelagohealth|pelagohealth-dev):\/{3,}/i, '$1://');
}

/** Whether this URL belongs to the previous app's link estate. */
export function isLegacyDeeplinkUrl(url: string): boolean {
  if (LEGACY_SCHEME_RE.test(normalizeLegacyPathStyle(url))) return true;
  if (!url.startsWith('https://')) return false;
  try {
    const host = new URL(url).hostname.toLowerCase();
    return LEGACY_APP_LINK_HOSTS.some((candidate) => candidate === host);
  } catch {
    return false;
  }
}

function canonical(screenId: ScreenId, search: string): string {
  return search ? `galapagos://${screenId}?${search}` : `galapagos://${screenId}`;
}

/**
 * Translate a legacy link to its canonical Galapagos URL, or `null` when the
 * URL is not a legacy link at all (callers then hand it to the canonical grammar
 * untouched). A legacy link always translates — an unknown surface resolves
 * to the home fallback, because "opens the app somewhere sensible" is the
 * contract the previous manifest made and a browser tab is never right.
 */
export function translateLegacyDeeplinkUrl(url: string): string | null {
  const schemeMatch = normalizeLegacyPathStyle(url).match(LEGACY_SCHEME_RE);
  if (schemeMatch) {
    const surface = schemeMatch[2].toLowerCase();
    return canonical(
      LEGACY_SURFACE_SCREEN_IDS[surface] ?? LEGACY_FALLBACK_SCREEN_ID,
      schemeMatch[3] ?? '',
    );
  }
  if (!url.startsWith('https://')) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  const search = parsed.search.replace(/^\?/, '');
  if (host === 'app.pelagohealth.com') {
    const surface = parsed.pathname.split('/').filter(Boolean)[0]?.toLowerCase() ?? 'home';
    return canonical(LEGACY_SURFACE_SCREEN_IDS[surface] ?? LEGACY_FALLBACK_SCREEN_ID, search);
  }
  if (host === 'links.pelagohealth.com') {
    return canonical(LEGACY_FALLBACK_SCREEN_ID, search);
  }
  return null;
}
